import React from 'react';

import { useIntl } from 'react-intl';
import { Row, Col, Form, Select, FormInstance, Input } from 'antd';

import { useAddressQuery } from '@/hooks';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { ReportSuppliersFormValue } from '@/core/domain/reports/people/Report.types';

interface Props {
  form?: FormInstance<ReportSuppliersFormValue>;
}

function SuppliersReportForm({ form }: Props) {
  const intl = useIntl();

  const { state, city } = useAddressQuery();

  const handleFormsValuesChange = (
    valuesChanged: ReportSuppliersFormValue,
    fieldValue: ReportSuppliersFormValue,
  ) => {
    handleStateChange(valuesChanged);

    if (!fieldValue.uf) {
      form?.resetFields(['city']);
      state.setSelectedStateCode('');
    }
  };

  function handleStateChange(values: ReportSuppliersFormValue) {
    if (!values.uf) return;

    form?.resetFields(['city']);

    state.setSelectedStateCode(values.uf);
  }

  return (
    <Form form={form} onValuesChange={handleFormsValuesChange}>
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.modal.report.form.type',
            })}
            name="companyType"
          >
            <Select
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={[
                {
                  value: 'INDIVIDUAL',
                  label: intl.formatMessage({
                    id: 'suppliers.general.form.type.individual',
                  }),
                },
                {
                  value: 'LEGAL_PERSON',
                  label: intl.formatMessage({
                    id: 'suppliers.general.form.type.legalPerson',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.modal.report.form.situation',
            })}
            name="active"
          >
            <Select
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={[
                {
                  value: 0,
                  label: intl.formatMessage({
                    id: 'suppliers.filters.active.options.all',
                  }),
                },
                {
                  value: 1,
                  label: intl.formatMessage({
                    id: 'suppliers.filters.active.options.active',
                  }),
                },
                {
                  value: 2,
                  label: intl.formatMessage({
                    id: 'suppliers.filters.active.options.inactive',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.modal.report.form.state',
            })}
            name="uf"
          >
            <Select
              allowClear
              showSearch
              loading={state.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={state.list.map((s) => ({
                value: s.code,
                label: s.code,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.modal.report.form.city',
            })}
            name="city"
          >
            <Select
              allowClear
              showSearch
              loading={city.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={city.list.map((c) => ({
                value: c.name,
                label: c.name,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.modal.report.form.pieces',
            })}
            name="product"
          >
            <Input />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default SuppliersReportForm;
